import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Crown, Droplets, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import PlantCard from '../components/PlantCard';
import AddPlantModal from '../components/AddPlantModal';
import PlantDetailModal from '../components/PlantDetailModal';
import EdenWallet from '../components/EdenWallet';
import './MyGarden.css';

const FREE_PLANT_LIMIT = 10;

export default function MinHave() {
    const { t, locale } = useI18n();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [adding, setAdding] = useState(false);
    const [detail, setDetail] = useState(null);
    const [wateringAll, setWateringAll] = useState(false);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const da = locale === 'da';

    const load = () => api.get('/plants').then((r) => { setPlants(r.data || []); setError(false); })
        .catch(() => setError(true)).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const isPremium = !!user?.premium;
    const count = plants.length;
    const pct = Math.min(100, Math.round((count / FREE_PLANT_LIMIT) * 100));
    const atLimit = count >= FREE_PLANT_LIMIT;
    const nearLimit = count >= FREE_PLANT_LIMIT - 2; // 8, 9, 10
    const barColor = atLimit ? 'bg-brand-terracotta' : nearLimit ? 'bg-brand-ochre' : 'bg-brand-green';

    const waterAll = async () => {
        if (!plants.length || wateringAll) return;
        setWateringAll(true);
        try {
            await api.post('/plants/water-all');
            await load();
        } catch {
            setError(true);
        } finally {
            setWateringAll(false);
        }
    };

    return (
        <div className="my-garden px-5 pt-10 pb-32 mx-auto">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{count} plants</p>
                    <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1">{t('garden.title')}</h1>
                </div>
                <button
                    data-testid="btn-add-plant"
                    onClick={() => setAdding(true)}
                    className="inline-flex items-center gap-1.5 bg-brand-green text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-brand-greenDark transition-colors"
                >
                    <Plus size={15} strokeWidth={2.2} /> {t('garden.add')}
                </button>
            </div>

            <EdenWallet />
            <Link to="/calendar?scope=garden" className="mb-5 inline-flex items-center gap-2 px-4 py-3 rounded-2xl border border-brand-border text-brand-ink">📅 {da ? 'Min plantekalender' : 'My plant calendar'}</Link>

            {plants.length > 1 && (
                <button
                    data-testid="btn-water-all"
                    onClick={waterAll}
                    disabled={wateringAll}
                    className="mb-5 w-full inline-flex items-center justify-center gap-2 bg-sky-500/10 border border-sky-500/25 text-sky-700 dark:text-sky-300 px-4 py-3 rounded-2xl text-sm font-medium hover:bg-sky-500/15 disabled:opacity-60"
                >
                    {wateringAll ? <Loader2 size={16} className="animate-spin" /> : <Droplets size={16} />}
                    {wateringAll ? 'Updating garden…' : `Water all ${plants.length} plants now`}
                </button>
            )}

            {!isPremium && (
                <div
                    data-testid="plant-limit-progress"
                    className={`mb-6 rounded-2xl border p-4 transition-colors ${atLimit ? 'border-brand-terracotta/40 bg-brand-terracotta/5' : nearLimit ? 'border-brand-ochre/40 bg-brand-ochre/5' : 'border-brand-border bg-brand-card'}`}
                >
                    <div className="flex items-baseline justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Free plan</span>
                        <span data-testid="plant-limit-count" className="text-xs font-medium text-brand-ink2">
                            {Math.min(count, FREE_PLANT_LIMIT)} <span className="text-brand-ink3">/ {FREE_PLANT_LIMIT} plants</span>
                        </span>
                    </div>
                    <div className="h-2 bg-brand-bg2 rounded-full overflow-hidden">
                        <div
                            data-testid="plant-limit-bar"
                            className={`h-full ${barColor} transition-all duration-500 ease-out`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    {nearLimit && (
                        <button
                            data-testid="btn-upgrade-from-limit"
                            onClick={() => navigate('/premium')}
                            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-brand-green hover:bg-brand-greenDark text-white text-xs font-medium px-4 py-2 rounded-full transition-colors"
                        >
                            <Crown size={13} strokeWidth={2} />
                            {atLimit ? 'Limit reached — go Premium for unlimited plants' : `${FREE_PLANT_LIMIT - count} left — unlock unlimited with Premium`}
                        </button>
                    )}
                </div>
            )}

            <label className="my-garden-search">
                <span>{da ? 'Find i din have' : 'Find in your garden'}</span>
                <input type="search" value={query} onChange={e => setQuery(e.target.value)}
                    placeholder={da ? 'Navn, art eller sort…' : 'Name, species or variety…'} />
            </label>
            {error && <div role="alert" className="my-garden-error">
                {da ? 'Kunne ikke opdatere haven. Prøv igen.' : 'Could not update the garden. Please retry.'}
                <button onClick={load}>{da ? 'Prøv igen' : 'Retry'}</button>
            </div>}
            {loading ? <p role="status">{da ? 'Henter din have…' : 'Loading your garden…'}</p> : plants.length === 0 && !error ? (
                <button onClick={() => setAdding(true)} className="w-full bg-brand-card border border-dashed border-brand-border rounded-3xl p-12 text-center hover:border-brand-green transition-colors">
                    <div className="text-5xl mb-3">🌱</div>
                    <p className="text-sm text-brand-ink2">{t('garden.empty')}</p>
                </button>
            ) : (
                <div className="my-garden-grid">
                    {plants.filter(p => [p.name, p.display_name, p.species_common_name, p.species, p.variety]
                        .some(value => String(value || '').toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()))).map((p) => (
                        <PlantCard key={p.id} plant={p} onChanged={load} onOpen={setDetail} />
                    ))}
                </div>
            )}
            {!loading && plants.length > 0 && !plants.some(p => [p.name, p.display_name, p.species_common_name, p.species, p.variety]
                .some(value => String(value || '').toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()))) &&
                <p role="status">{da ? 'Ingen planter matcher din søgning.' : 'No plants match your search.'}</p>}

            <AddPlantModal open={adding} onClose={() => setAdding(false)} onAdded={() => load()} />
            <PlantDetailModal plant={detail} open={!!detail} onClose={() => setDetail(null)} onChanged={load} />
        </div>
    );
}
